import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import type { Database } from "@/integrations/supabase/types";

type CategoryEnum = Database["public"]["Enums"]["expense_category"];
type FrequencyEnum = Database["public"]["Enums"]["expense_frequency"];

export type ExpenseRow = {
  id: string;
  occurred_on: string;
  category: CategoryEnum;
  amount_cents: number;
  currency: string;
  vendor: string | null;
  note: string | null;
  frequency: FrequencyEnum;
  environment: "sandbox" | "live";
};

const CATEGORIES: Array<{ value: string; label: string }> = [
  { value: "hosting", label: "Hosting / Cloud" },
  { value: "ai_api", label: "AI / API" },
  { value: "contractors", label: "Contractors" },
  { value: "salaries", label: "Salaries" },
  { value: "tools_saas", label: "Tools & SaaS" },
  { value: "marketing", label: "Marketing" },
  { value: "legal_accounting", label: "Legal & Accounting" },
  { value: "taxes", label: "Taxes" },
  { value: "refunds_manual", label: "Refunds (manual)" },
  { value: "other", label: "Other" },
];

const today = () => new Date().toISOString().slice(0, 10);

export const ExpenseDialog = ({
  open,
  onOpenChange,
  environment,
  existing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  environment: "sandbox" | "live";
  existing: ExpenseRow | null;
  onSaved: () => void;
}) => {
  const { user } = useAuth();
  const [date, setDate] = useState(today());
  const [category, setCategory] = useState<CategoryEnum>("hosting");
  const [amount, setAmount] = useState("0.00");
  const [vendor, setVendor] = useState("");
  const [note, setNote] = useState("");
  const [frequency, setFrequency] = useState<"one_time" | "monthly" | "yearly">("one_time");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setDate(existing.occurred_on);
      setCategory(existing.category);
      setAmount((existing.amount_cents / 100).toFixed(2));
      setVendor(existing.vendor ?? "");
      setNote(existing.note ?? "");
      setFrequency(existing.frequency);
    } else {
      setDate(today());
      setCategory("hosting");
      setAmount("0.00");
      setVendor("");
      setNote("");
      setFrequency("one_time");
    }
  }, [open, existing]);

  const save = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!Number.isFinite(cents) || cents < 0) { toast.error("Amount must be a non-negative number"); return; }
    if (!user?.id) { toast.error("Not signed in"); return; }
    setSaving(true);
    if (existing) {
      const { error } = await supabase
        .from("operating_expenses")
        .update({
          occurred_on: date,
          category: category as ExpenseRow["category"],
          amount_cents: cents,
          vendor: vendor || null,
          note: note || null,
          frequency,
        })
        .eq("id", existing.id);
      setSaving(false);
      if (error) { toast.error(error.message); return; }
    } else {
      const { error } = await supabase.from("operating_expenses").insert({
        occurred_on: date,
        category: category as ExpenseRow["category"],
        amount_cents: cents,
        currency: "USD",
        vendor: vendor || null,
        note: note || null,
        frequency,
        environment,
        created_by: user.id,
      });
      setSaving(false);
      if (error) { toast.error(error.message); return; }
    }
    toast.success(existing ? "Expense updated" : "Expense added");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existing ? "Edit expense" : "Add expense"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <Label>Amount (USD)</Label>
              <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as CategoryEnum)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={frequency} onValueChange={(v) => setFrequency(v as typeof frequency)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-time</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly (amortized)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Vendor (optional)</Label>
            <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="e.g. AWS, Anthropic" />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </div>
          <p className="text-xs text-muted-foreground">Environment: <strong>{environment}</strong></p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
